import type {
  StatusPageComponent,
  StatusPageSeverity,
  StatusPageSummary,
} from '../../core/interfaces/status-page.interface';

const SEVERITY_BY_STATUS: Readonly<Record<string, StatusPageSeverity>> = {
  operational: 'operational',
  under_maintenance: 'maintenance',
  degraded_performance: 'degraded',
  partial_outage: 'partial',
  major_outage: 'major',
};

const SEVERITY_ORDER: readonly StatusPageSeverity[] = ['operational', 'maintenance', 'degraded', 'partial', 'major'];

/**
 * Reads the components of a status page API response, ignoring anything malformed.
 *
 * @param payload - Parsed JSON of `components.json`.
 */
export function parseStatusPageComponents(payload: unknown): StatusPageComponent[] {
  if (typeof payload !== 'object' || payload === null) return [];
  const components = (payload as { components?: unknown }).components; // third-party JSON, read defensively
  if (!Array.isArray(components)) return [];
  return components.flatMap((item): StatusPageComponent[] => {
    if (typeof item !== 'object' || item === null) return [];
    const { name, status, group } = item as { name?: unknown; status?: unknown; group?: unknown };
    return typeof name === 'string' && typeof status === 'string' ? [{ name, status, group: group === true }] : [];
  });
}

/**
 * Summarizes the status page: how many real components are operational, how many are not, and the
 * worst situation (an unknown status counts as a degraded one so it is never hidden).
 *
 * @param components - Components as returned by `parseStatusPageComponents`.
 * @example
 * summarizeComponents([{ name: 'API', status: 'partial_outage' }]).worst; // 'partial'
 */
export function summarizeComponents(components: readonly StatusPageComponent[]): StatusPageSummary {
  const real = components.filter((c) => !c.group);
  const operational = real.filter((c) => c.status === 'operational').length;
  const worst = real.reduce<StatusPageSeverity>((current, c) => {
    const severity = SEVERITY_BY_STATUS[c.status] ?? 'degraded';
    return SEVERITY_ORDER.indexOf(severity) > SEVERITY_ORDER.indexOf(current) ? severity : current;
  }, 'operational');
  return { total: real.length, operational, problems: real.length - operational, worst };
}
